/**
 * Ask a question from anywhere, await the answer.
 *
 * A module store rather than per-host state, for the reason deck15/directory/overlays.ts already
 * proved: the OPENER is usually a callback deep in an action dispatcher and the RENDERER is one
 * host near the root. Threading a setter through every hook's prop bag made each call site carry a
 * concern only the host has — which is a large part of why seven separate dialogs existed.
 *
 *   const { action } = await askConfirm({ title: 'Delete this canvas?', body, confirmLabel: 'Delete' });
 *   if (action !== 'confirm') return;
 *
 * One at a time. A second ask while one is open cancels the first rather than stacking: two
 * questions on one screen is two answers to one screen.
 */
import type { ConfirmDialogProps } from './ConfirmDialog';

/**
 * `cancel` is the cancel BUTTON. `dismiss` is Escape or a backdrop press.
 *
 * They differ only where the quiet button is itself destructive — the three-way's "Discard".
 * Everywhere else both mean no, which is why the common `action !== 'confirm'` guard is still
 * the right thing to write and needs no updating.
 */
export type ConfirmAction = 'confirm' | 'secondary' | 'cancel' | 'dismiss';

export interface ConfirmOutcome {
  action: ConfirmAction;
  /** The field's trimmed value, or '' when the request had no input. */
  value: string;
}

export type ConfirmRequest = Omit<
  ConfirmDialogProps,
  'onConfirm' | 'onCancel' | 'onDismiss' | 'secondary' | 'theme'
> & {
  /** The third answer. Its label only — the outcome comes back as action: 'secondary'. */
  secondaryLabel?: string;
};

interface PendingConfirm {
  id: number;
  request: ConfirmRequest;
  settle: (outcome: ConfirmOutcome) => void;
}

const listeners = new Set<() => void>();
let pending: PendingConfirm | null = null;
let nextId = 1;

function bump(): void {
  for (const listener of listeners) listener();
}

export function subscribeConfirm(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function currentConfirm(): PendingConfirm | null {
  return pending;
}

/** Nothing is ever pending during SSR, and useSyncExternalStore needs a stable server snapshot. */
export function confirmServerSnapshot(): null {
  return null;
}

export function askConfirm(request: ConfirmRequest): Promise<ConfirmOutcome> {
  return new Promise<ConfirmOutcome>((resolve) => {
    // Supersede rather than stack. The previous asker hears 'cancel', so its `if (action !==
    // 'confirm') return` guard does the right thing and nothing is left half-committed.
    pending?.settle({ action: 'cancel', value: '' });
    pending = { id: nextId++, request, settle: resolve };
    bump();
  });
}

/** The common case: a plain yes/no where only "did they say yes" matters. */
export async function confirmed(request: ConfirmRequest): Promise<boolean> {
  const { action } = await askConfirm(request);
  return action === 'confirm';
}

export function resolveConfirm(id: number, outcome: ConfirmOutcome): void {
  if (pending === null || pending.id !== id) return;
  const { settle } = pending;
  pending = null;
  bump();
  settle(outcome);
}
